/**
 * DYNAMIC FORM RENDERER — Renders a form based on field definitions from the API.
 * Supports all 12 field types, section grouping, validation display, width/layout.
 */
import React from 'react';
import {
  Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Checkbox, Radio, RadioGroup, FormLabel,
  Typography, Card, CardContent, Stack, Chip, FormHelperText,
  InputAdornment,
} from '@mui/material';

const CURRENCY_SYMBOL = '\u20ac';

export default function DynamicFormRenderer({
  fields = [],
  sections = [],
  formType = 'create',
  values = {},
  onChange,
  errors = {},
  readOnly = false,
}) {
  const visibilityKey = formType === 'create' ? 'show_in_create'
    : formType === 'edit' ? 'show_in_edit'
    : 'show_in_detail';

  const visibleFields = fields
    .filter(f => f.is_active && f[visibilityKey])
    .sort((a, b) => a.sort_order - b.sort_order);

  const activeSections = sections
    .filter(s => s.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);

  const handleChange = (key, value) => {
    if (onChange) onChange({ ...values, [key]: value });
  };

  const renderField = (field) => {
    const raw = values[field.key];
    const value = raw !== undefined && raw !== null ? raw : (field.default_value ?? '');
    const error = errors[field.key];
    const isReadOnly = readOnly || formType === 'detail';
    const width = parseInt(field.width) || 6;
    const gridSize = Math.min(12, Math.max(1, width));

    const commonTextProps = {
      fullWidth: true,
      size: 'small',
      error: !!error,
      helperText: error || field.help_text || '',
      disabled: isReadOnly,
      placeholder: field.placeholder || '',
      required: field.is_required,
    };

    let input;

    switch (field.field_type) {
      case 'text':
        input = (
          <TextField {...commonTextProps} label={field.label} value={value}
            onChange={e => handleChange(field.key, e.target.value)} />
        );
        break;

      case 'email':
        input = (
          <TextField {...commonTextProps} label={field.label} type="email" value={value}
            onChange={e => handleChange(field.key, e.target.value)} />
        );
        break;

      case 'phone':
        input = (
          <TextField {...commonTextProps} label={field.label} type="tel" value={value}
            onChange={e => handleChange(field.key, e.target.value)} />
        );
        break;

      case 'textarea':
        input = (
          <TextField {...commonTextProps} label={field.label} value={value}
            onChange={e => handleChange(field.key, e.target.value)}
            multiline rows={3} />
        );
        break;

      case 'number':
        input = (
          <TextField {...commonTextProps} label={field.label} type="number" value={value}
            onChange={e => handleChange(field.key, e.target.value)}
            inputProps={{
              min: field.validation_min || undefined,
              max: field.validation_max || undefined,
              step: 'any',
            }} />
        );
        break;

      case 'currency':
        input = (
          <TextField {...commonTextProps} label={field.label} type="number" value={value}
            onChange={e => handleChange(field.key, e.target.value)}
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{CURRENCY_SYMBOL}</InputAdornment>,
            }} />
        );
        break;

      case 'date':
        input = (
          <TextField {...commonTextProps} label={field.label} type="date"
            value={value || ''}
            onChange={e => handleChange(field.key, e.target.value)}
            InputLabelProps={{ shrink: true }} />
        );
        break;

      case 'select': {
        const options = (field.options || []).filter(o => o.isActive !== false);
        input = (
          <FormControl fullWidth size="small" error={!!error} disabled={isReadOnly} required={field.is_required}>
            <InputLabel>{field.label}</InputLabel>
            <Select value={value || ''} label={field.label}
              onChange={e => handleChange(field.key, e.target.value)}>
              <MenuItem value=""><em>Zgjidh...</em></MenuItem>
              {options.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            {(error || field.help_text) && <FormHelperText error={!!error}>{error || field.help_text}</FormHelperText>}
          </FormControl>
        );
        break;
      }

      case 'multiselect': {
        const options = (field.options || []).filter(o => o.isActive !== false);
        const selectedValues = Array.isArray(value) ? value
          : (typeof value === 'string' && value ? value.split(',').filter(Boolean) : []);
        input = (
          <FormControl fullWidth size="small" error={!!error} disabled={isReadOnly}>
            <InputLabel>{field.label}</InputLabel>
            <Select multiple value={selectedValues} label={field.label}
              onChange={e => handleChange(field.key, e.target.value)}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                  {selected.map(v => {
                    const opt = options.find(o => o.value === v);
                    return <Chip key={v} label={opt?.label || v} size="small" />;
                  })}
                </Stack>
              )}>
              {options.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            {(error || field.help_text) && <FormHelperText error={!!error}>{error || field.help_text}</FormHelperText>}
          </FormControl>
        );
        break;
      }

      case 'checkbox':
        input = (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === 'true'}
                  onChange={e => handleChange(field.key, e.target.checked)}
                  disabled={isReadOnly}
                  size="small"
                />
              }
              label={field.label}
            />
            {(error || field.help_text) && (
              <FormHelperText error={!!error} sx={{ mt: -0.5, ml: 4 }}>{error || field.help_text}</FormHelperText>
            )}
          </Box>
        );
        break;

      case 'boolean':
        input = (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={value === true || value === 'true'}
                  onChange={e => handleChange(field.key, e.target.checked)}
                  disabled={isReadOnly}
                  size="small"
                />
              }
              label={field.label}
            />
            {(error || field.help_text) && (
              <FormHelperText error={!!error} sx={{ mt: -0.5, ml: 4 }}>{error || field.help_text}</FormHelperText>
            )}
          </Box>
        );
        break;

      case 'radio': {
        const options = (field.options || []).filter(o => o.isActive !== false);
        input = (
          <FormControl component="fieldset" error={!!error} disabled={isReadOnly}>
            <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>{field.label}{field.is_required && ' *'}</FormLabel>
            <RadioGroup value={value || ''} onChange={e => handleChange(field.key, e.target.value)} row>
              {options.map(opt => (
                <FormControlLabel key={opt.value} value={opt.value}
                  control={<Radio size="small" disabled={isReadOnly} />} label={opt.label} />
              ))}
            </RadioGroup>
            {(error || field.help_text) && <FormHelperText error={!!error}>{error || field.help_text}</FormHelperText>}
          </FormControl>
        );
        break;
      }

      default:
        input = (
          <TextField {...commonTextProps} label={field.label} value={value}
            onChange={e => handleChange(field.key, e.target.value)} />
        );
    }

    return (
      <Grid item xs={12} sm={gridSize} key={field.key}>
        {input}
      </Grid>
    );
  };

  // Group fields by section
  const sectionedFields = {};
  const unsectionedFields = [];
  for (const f of visibleFields) {
    if (f.section_id) {
      if (!sectionedFields[f.section_id]) sectionedFields[f.section_id] = [];
      sectionedFields[f.section_id].push(f);
    } else {
      unsectionedFields.push(f);
    }
  }

  return (
    <Box>
      {activeSections.map(section => {
        const sFields = sectionedFields[section.id] || [];
        if (sFields.length === 0) return null;
        return (
          <Card key={section.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ p: '8px 16px', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700}>{section.label}</Typography>
              {section.description && (
                <Typography variant="caption" color="text.secondary">{section.description}</Typography>
              )}
            </Box>
            <CardContent sx={{ pb: '16px !important' }}>
              <Grid container spacing={2}>
                {sFields.map(f => renderField(f))}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      {unsectionedFields.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Grid container spacing={2}>
              {unsectionedFields.map(f => renderField(f))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {visibleFields.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Asnj\u00eb fush\u00eb e konfiguruar p\u00ebr k\u00ebt\u00eb pamje.
        </Typography>
      )}
    </Box>
  );
}
